import { useCallback, useState } from "react";
import { useAuthSession } from "../../auth/shared";
import {
  getProfileErrorMessage,
  updateProfile,
  type UpdateProfileRequest,
} from "../api/profile.api";

interface UseUpdateProfileReturn {
  error: string | null;
  loading: boolean;
  reset: () => void;
  success: boolean;
  updateProfile: (request: UpdateProfileRequest) => Promise<void>;
}

export const useUpdateProfile = (): UseUpdateProfileReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { user, updateUser } = useAuthSession();

  const submitUpdate = useCallback(
    async (request: UpdateProfileRequest) => {
      if (loading) {
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const updatedProfile = await updateProfile(request);

        if (!user) {
          throw new Error("User session is unavailable");
        }

        updateUser({ ...user, ...updatedProfile });
        setSuccess(true);
      } catch (error: unknown) {
        setError(getProfileErrorMessage(error, "Failed to update profile"));
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [loading, updateUser, user],
  );

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return { updateProfile: submitUpdate, loading, error, success, reset };
};
